import React, { useEffect, useRef, useId } from "react";

const SquareOffersSlider = ({ title, products, showTitle = true }) => {
  const sliderRef = useRef(null);
  const uniqueId = useId().replace(/:/g, "-");
  const sliderClass = `tf-sw-product-sell-showers-${uniqueId}`;

  useEffect(() => {
    // Wait for DOM to be ready and Swiper to be available
    const initializeSlider = () => {
      if (!window.Swiper) {
        console.log("Swiper not available yet, retrying...");
        setTimeout(initializeSlider, 100);
        return;
      }

      // Initialize SquareOffersSlider using ref
      if (sliderRef.current) {
        // Destroy existing instance if it exists
        if (sliderRef.current.swiper) {
          try {
            sliderRef.current.swiper.destroy(true, true);
          } catch (e) {
            // no-op
          }
        }

        console.log("Initializing SquareOffersSlider...");
        const sliderElement = sliderRef.current;
        const preview = parseFloat(sliderElement.dataset.preview) || 4.6;
        const tablet = parseFloat(sliderElement.dataset.tablet) || 2.6;
        const mobile = parseFloat(sliderElement.dataset.mobile) || 2.5;
        const spacingLg = parseInt(sliderElement.dataset.spaceLg) || 30;
        const spacingMd = parseInt(sliderElement.dataset.spaceMd) || 15;
        const perGroup = parseInt(sliderElement.dataset.pagination) || 1;
        const perGroupMd = parseInt(sliderElement.dataset.paginationMd) || 1;
        const perGroupLg = parseInt(sliderElement.dataset.paginationLg) || 1;

        console.log("Mobile slidesPerView:", mobile);

        new window.Swiper(sliderRef.current, {
          slidesPerView: mobile,
          spaceBetween: spacingMd,
          speed: 1000,
          pagination: {
            el: `.sw-pagination-sell-showers-${uniqueId}`,
            clickable: true,
          },
          slidesPerGroup: perGroup,
          navigation: {
            clickable: true,
            nextEl: `.nav-next-showers-${uniqueId}`,
            prevEl: `.nav-prev-showers-${uniqueId}`,
          },
          breakpoints: {
            768: {
              slidesPerView: preview,
              spaceBetween: spacingLg,
              slidesPerGroup: perGroupLg,
            },
            1150: {
              slidesPerView: preview,
              spaceBetween: spacingLg,
              slidesPerGroup: perGroupLg,
            },
          },
          watchOverflow: true,
          observer: true,
          observeParents: true,
        });
        console.log("SquareOffersSlider initialized successfully");
      }
    };

    initializeSlider();
  }, [uniqueId]);

  return (
    <>
      <style>
        {`
          /* Maximum width constraint for square offers slider */
          .flat-spacing-27-edit {
            max-width: 1600px !important;
            margin: 0 auto !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
            box-sizing: border-box !important;
          }

          @media (max-width: 575px) {
            .flat-spacing-27-edit {
              max-width: 100% !important;
              padding-left: 12px !important;
              padding-right: 12px !important;
            }
          }

          @media (min-width: 576px) and (max-width: 767px) {
            .flat-spacing-27-edit {
              max-width: 100% !important;
              padding-left: 16px !important;
              padding-right: 16px !important;
            }
          }

          @media (min-width: 768px) and (max-width: 991px) {
            .flat-spacing-27-edit {
              max-width: 1200px !important;
              padding-left: 20px !important;
              padding-right: 20px !important;
            }
          }

          @media (min-width: 992px) and (max-width: 1199px) {
            .flat-spacing-27-edit {
              max-width: 1400px !important;
              padding-left: 20px !important;
              padding-right: 20px !important;
            }
          }

          @media (min-width: 1400px) {
            .flat-spacing-27-edit {
              max-width: 1600px !important;
              padding-left: 20px !important;
              padding-right: 20px !important;
            }
          }

          /* Ensure square aspect ratio for collection items */
          .${sliderClass} .swiper-slide {
            height: auto !important;
          }
          
          .${sliderClass} .collection-item-v3 {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          
          .${sliderClass} .collection-item-v3 .collection-image {
            position: relative;
            width: 100%;
            padding-top: 100%; /* 1:1 aspect ratio for square */
            overflow: hidden;
          }
          
          .${sliderClass} .collection-item-v3 .collection-image img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
          }
          
          /* Blue hover effect for product titles */
          .${sliderClass} .collection-item-v3 .collection-content .title {
            transition: color 0.3s ease;
          }
          
          .${sliderClass} .collection-item-v3:hover .collection-content .title {
            color: #0e5aa7 !important;
          }
          
          /* Hide arrow icons on slider items */
          .${sliderClass} .collection-item-v3 .collection-image .box-icon {
            display: none !important;
          }
          
          .${sliderClass} .collection-item-v3 .collection-image::before,
          .${sliderClass} .collection-item-v3 .collection-image::after {
            display: none !important;
          }
          
          /* Make bluish border always visible */
          .${sliderClass} .collection-item-v3 .collection-image {
            border-color: #0e5aa7 !important;
            outline-color: #0e5aa7 !important;
            outline: 1px solid #0e5aa7 !important;
            border: 1px solid #0e5aa7 !important;
          }
          
          /* Enhance border on hover */
          .${sliderClass} .collection-item-v3:hover .collection-image,
          .${sliderClass} .collection-item-v3 .collection-image:hover,
          .${sliderClass} .collection-item-v3 .collection-image:focus,
          .${sliderClass} .collection-item-v3 .collection-image:focus-visible {
            border-color: #0a4a8a !important;
            outline-color: #0a4a8a !important;
            outline: 2px solid #0a4a8a !important;
            border: 2px solid #0a4a8a !important;
          }
          
          /* Adjust items for mobile - ensure correct width for 2.5 items */
          @media (max-width: 767px) {
            .${sliderClass} .swiper-slide {
              width: calc((100% - 30px) / 2.5) !important;
              flex-shrink: 0 !important;
              margin-right: 15px !important;
              height: auto !important;
            }
            
            .${sliderClass} .collection-item-v3 {
              width: 100%;
              min-width: 0;
              min-height: 180px;
            }
            
            .${sliderClass} .collection-item-v3 .collection-content {
              min-height: 40px;
              padding: 6px 12px;
              margin-top: 0 !important;
              padding-top: 2px !important;
            }
            
            .${sliderClass} .collection-item-v3 .collection-image {
              margin-bottom: 0 !important;
              padding-bottom: 0 !important;
            }
          }
          
          /* Increase height to accommodate text on all screens */
          .${sliderClass} .swiper-slide {
            height: auto !important;
          }
          
          .${sliderClass} .collection-item-v3 {
            display: flex;
            flex-direction: column;
            height: 100%;
            min-height: 200px;
          }
          
          .${sliderClass} .collection-item-v3 .collection-image {
            flex-shrink: 0;
          }
          
          .${sliderClass} .collection-item-v3 .collection-content {
            flex: 1;
            display: flex;
            align-items: center;
            min-height: 45px;
            padding: 8px 12px;
            margin-top: 0 !important;
            padding-top: 2px !important;
          }
          
          .${sliderClass} .collection-item-v3 .collection-image {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
        `}
      </style>
      <section className="flat-spacing-27-edit" style={{ paddingTop: "60px" }}>
        <div className="container">
          <div
            className="flat-title flex-row justify-content-between px-0"
            style={{ marginBottom: "24px" }}
          >
            {showTitle && title && (
              <span
                className="title wow fadeInUp text_green-1"
                data-wow-delay="0s"
                style={{ fontSize: "1.6rem", fontWeight: "600" }}
              >
                {title}
              </span>
            )}
            {/* <div className="box-sw-navigation">
            <div className="nav-sw round nav-next-showers nav-next-slider">
              <span className="icon icon-arrow-left"></span>
            </div>
            <div className="nav-sw round nav-prev-showers nav-prev-slider">
              <span className="icon icon-arrow-right"></span>
            </div>
          </div> */}
          </div>
        </div>
        <div className="container-full slider-layout-right">
          <div
            ref={sliderRef}
            dir="ltr"
            className={`swiper ${sliderClass} sw-wrapper-right`}
            data-preview="4.6"
            data-tablet="2.6"
            data-mobile="2.5"
            data-space-lg="30"
            data-space-md="15"
            data-pagination="1"
            data-pagination-md="1"
            data-pagination-lg="1"
          >
            <div className="swiper-wrapper">
              {products.map((product, index) => (
                <div key={index} className="swiper-slide">
                  <div className="collection-item-v3 hover-img">
                    <a
                      href={product.link || "/shop-collection-sub"}
                      className="collection-image img-style"
                    >
                      <img src={product.image} alt={product.name} />
                    </a>
                    <div className="collection-content collection-content-before">
                      <a
                        href={product.link || "/shop-collection-sub"}
                        className="link title fw-5 text_green-1"
                      >
                        {product.name}
                      </a>
                      {/* <div className="count text_black-2">
                      {product.count || "0 items"}
                    </div> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SquareOffersSlider;
